import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listUsers,
  getUser,
  createUserCall,
  setUserRoleCall,
  setUserActiveCall,
  type CreateUserInput,
  requestPasswordResetLink,
  setUserPasswordCall,
} from '@/lib/api/users';
import type { UserRole } from '@/types/domain';

const KEYS = {
  all: ['users'] as const,
  list: () => [...KEYS.all, 'list'] as const,
  detail: (uid: string) => [...KEYS.all, 'detail', uid] as const,
};

export const useUsers = () =>
  useQuery({
    queryKey: KEYS.list(),
    queryFn: () => listUsers(),
    staleTime: 30 * 1000,
  });

export const useUser = (uid: string | null) =>
  useQuery({
    queryKey: KEYS.detail(uid ?? ''),
    queryFn: () => (uid ? getUser(uid) : Promise.resolve(null)),
    enabled: !!uid,
  });

export const useCreateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => createUserCall(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

export const useSetUserRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, role }: { uid: string; role: UserRole }) =>
      setUserRoleCall(uid, role),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

export const useSetUserActive = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ uid, disabled }: { uid: string; disabled: boolean }) =>
      setUserActiveCall(uid, disabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
};

/**
 * No cache invalidation: issuing a link changes nothing about the user record,
 * and the result is a secret we do not want sitting in the query cache.
 */
export const useRequestPasswordResetLink = () =>
  useMutation({ mutationFn: (email: string) => requestPasswordResetLink(email) });

export const useSetUserPassword = () =>
  useMutation({
    mutationFn: ({ uid, password }: { uid: string; password: string }) =>
      setUserPasswordCall(uid, password),
  });
